import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { ToggleLeft, Film, Clapperboard, TreePine, Layout, BookOpen, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

export default function CoupleFeatures() {
  const { wedding, setWedding } = useOutletContext();
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState(wedding?.features || {
    personal_sde: false, interactive_story: false,
    wedding_tree: true, landing_page: true, our_story_mode: false
  });
  const [landingMode, setLandingMode] = useState(wedding?.landing_page_mode || "standard");

  if (!wedding) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-wedding-ivory/60">No wedding assigned.</p>
      </div>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/weddings/${wedding.wedding_id}`,
        { features, landing_page_mode: landingMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWedding(response.data);
      toast.success("Feature settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const featureList = [
    { key: "personal_sde", label: "Personal SDE", desc: "Multiple Same-Day Edit perspectives (Groom, Bride, Best Man, etc.)", icon: Clapperboard },
    { key: "interactive_story", label: "Interactive Story", desc: "'How We Met' interactive video experience with questions", icon: BookOpen },
    { key: "wedding_tree", label: "Wedding Tree", desc: "Guest video message constellation", icon: TreePine },
    { key: "landing_page", label: "Landing Page", desc: "Wedding memory landing page", icon: Layout },
    { key: "our_story_mode", label: "Our Story Mode", desc: "Narrative-driven landing page with timeline", icon: Film },
  ];

  return (
    <div className="space-y-8" data-testid="couple-features">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Experience Modules</h1>
          <p className="text-wedding-ivory/60 mt-1">Toggle premium features for your wedding</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-features-btn">
          {saving ? <div className="w-5 h-5 border-2 border-wedding-black border-t-transparent rounded-full animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save</>}
        </Button>
      </div>

      {/* Feature Toggles */}
      <div className="space-y-4">
        {featureList.map((feature, index) => (
          <motion.div
            key={feature.key}
            className="flex items-center justify-between p-6 bg-wedding-charcoal border border-white/5 hover:border-wedding-gold/20 transition-colors"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-wedding-gold/10 flex items-center justify-center">
                <feature.icon className="w-5 h-5 text-wedding-gold" />
              </div>
              <div>
                <p className="text-wedding-ivory font-medium">{feature.label}</p>
                <p className="text-wedding-ivory/50 text-sm">{feature.desc}</p>
              </div>
            </div>
            <Switch
              checked={features[feature.key] || false}
              onCheckedChange={(checked) => setFeatures({ ...features, [feature.key]: checked })}
              data-testid={`toggle-${feature.key}`}
            />
          </motion.div>
        ))}
      </div>

      {/* Landing Page Mode */}
      <div className="p-6 bg-wedding-charcoal border border-white/5">
        <h2 className="font-serif text-xl text-wedding-ivory mb-4">Landing Page Mode</h2>
        <Select value={landingMode} onValueChange={setLandingMode}>
          <SelectTrigger className="w-full bg-wedding-subtle border-white/10" data-testid="landing-mode-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wedding-charcoal border-white/10">
            <SelectItem value="standard">Standard — Classic wedding page layout</SelectItem>
            <SelectItem value="our_story">Our Story — Narrative with timeline & interactive sections</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
